(() => {
  class PahiWelcomeDiscount {
    static PARAM = 'pahi_welcome';
    static COOKIE = 'pahi_welcome';
    static APPLIED_COOKIE = 'pahi_welcome_applied';

    constructor() {
      this._subscribed = false;
    }

    setLoadingState(on) {
      try {
        document.documentElement.classList.toggle('pahi-welcome-discount-loading', !!on);
      } catch (_e) {
        // ignore
      }
    }

    init() {
      try {
        const settings = window.pahiWelcomeDiscount || {};
        if (settings.enabled !== true) return;
        if (settings.code == null || String(settings.code).trim() === '') return;

        this.handleUrlParam();
        this.runEligibilityFlow();
      } catch (e) {
        // Never break storefront rendering
        // noop
      }
    }

    handleUrlParam() {
      const url = new URL(window.location.href);
      const value = url.searchParams.get(PahiWelcomeDiscount.PARAM);
      if (value !== 'true') return;

      // Gate for later steps / later pages
      this.setCookie(PahiWelcomeDiscount.COOKIE, 'true', { days: 7 });

      // Remove only our param, keep other params + hash
      url.searchParams.delete(PahiWelcomeDiscount.PARAM);
      const next =
        url.pathname +
        (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') +
        url.hash;
      window.history.replaceState({}, document.title, next);
    }

    runEligibilityFlow() {
      const customer = window.pahiCustomer || { loggedIn: false, ordersCount: 0 };
      const loggedIn = !!customer.loggedIn;
      const ordersCount = Number(customer.ordersCount || 0);

      // Logged-in customers with >= 1 order are not eligible
      if (loggedIn && ordersCount >= 1) {
        this.removeCookie(PahiWelcomeDiscount.COOKIE);
        this.removeCookie(PahiWelcomeDiscount.APPLIED_COOKIE);
        this.clearDiscountFromCart();
        return;
      }

      // Must have welcome cookie to continue (cookie is source of truth)
      if (!this.getCookie(PahiWelcomeDiscount.COOKIE)) return;

      // Guest users are always eligible; logged-in users with 0 orders are eligible.

      this.checkCartThenMaybeApply();
      this.subscribeToAddToCart();
    }

    getConfiguredDiscountCode() {
      const rawCode = String(window.pahiWelcomeDiscount && window.pahiWelcomeDiscount.code ? window.pahiWelcomeDiscount.code : '').trim();
      return rawCode || null;
    }

    cartHasDiscountCode(cart, code) {
      try {
        const codes = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        return codes.some((c) => {
          const v = c && (c.code || c.title || c.name);
          return v && String(v).toLowerCase() === String(code).toLowerCase();
        });
      } catch (_e) {
        return false;
      }
    }

    async reconcileAppliedCookieWithCart(cart) {
      // If cart no longer has the configured code, allow re-apply by clearing the flag.
      const code = this.getConfiguredDiscountCode();
      if (!code) return;

      const hasAppliedCookie = !!this.getCookie(PahiWelcomeDiscount.APPLIED_COOKIE);
      if (!hasAppliedCookie) return;

      const stillApplied = this.cartHasDiscountCode(cart, code);
      if (!stillApplied) {
        this.removeCookie(PahiWelcomeDiscount.APPLIED_COOKIE);
      }
    }

    async checkCartThenMaybeApply() {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      try {
        const res = await fetch(`${cartUrl}.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        const cart = await res.json();

        // If user removed discount later (checkout/cart), clear applied cookie so we can re-apply.
        await this.reconcileAppliedCookieWithCart(cart);

        if (cart && typeof cart.item_count === 'number' && cart.item_count > 0) {
          this.applyDiscountOnce('cart-non-empty');
        }
      } catch (_e) {
        // Ignore; we'll still rely on cartUpdate event
      }
    }

    subscribeToAddToCart() {
      if (this._subscribed) return;
      this._subscribed = true;

      // Prefer theme pubsub (custom-event style). If not present, fall back to nothing.
      const subscribeFn =
        (typeof subscribe === 'function' && subscribe) ||
        (typeof window.subscribe === 'function' && window.subscribe) ||
        null;
      const cartUpdateEventName =
        (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) ||
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        null;

      if (subscribeFn && cartUpdateEventName) {
        subscribeFn(cartUpdateEventName, (_event) => {
          // Re-check gates at the moment of ATC
          if (!this.getCookie(PahiWelcomeDiscount.COOKIE)) return;

          const customer = window.pahiCustomer || { loggedIn: false, ordersCount: 0 };
          const ordersCount = Number(customer.ordersCount || 0);
          const loggedIn = !!customer.loggedIn;
          if (loggedIn && ordersCount >= 1) {
            this.removeCookie(PahiWelcomeDiscount.COOKIE);
            this.removeCookie(PahiWelcomeDiscount.APPLIED_COOKIE);
            this.clearDiscountFromCart();
            return;
          }

          // At this point, cart was updated via ATC somewhere (PDP, quick add, bulk, etc.)
          this.applyDiscountOnce('cart-update');
        });
      }
    }

    async applyDiscountOnce(reason) {
      // Double guard to avoid race conditions
      if (this.getCookie(PahiWelcomeDiscount.APPLIED_COOKIE)) return;
      if (this._applyingDiscount) return;
      this._applyingDiscount = true;
      this.setLoadingState(true);

      const rawCode = this.getConfiguredDiscountCode();
      if (!rawCode) {
        this._applyingDiscount = false;
        return;
      }

      const cartUpdateUrl =
        (window.routes && window.routes.cart_update_url ? `${window.routes.cart_update_url}.js` : null) ||
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? `${window.Shopify.routes.root}cart/update.js`
          : '/cart/update.js');

      const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('discount', rawCode);

      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        // Dawn-style section rendering (ensures drawer HTML updates without page redirect)
        formData.append(
          'sections',
          cartEl.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      if (config.headers['Content-Type']) delete config.headers['Content-Type'];
      config.body = formData;

      try {
        const res = await fetch(cartUpdateUrl, config);
        const data = await res.json();
        if (data && data.status) {
          this._applyingDiscount = false;
          this.setLoadingState(false);
          return;
        }

        // Mark applied only after successful update call
        this.setCookie(PahiWelcomeDiscount.APPLIED_COOKIE, 'true', { days: 1 });

        // If we got section HTML back, re-render drawer/notification immediately
        if (cartEl && typeof cartEl.renderContents === 'function' && data && data.sections) {
          cartEl.renderContents(data);
        } else if (cartEl && cartEl.tagName === 'CART-DRAWER' && typeof cartEl.open === 'function') {
          // Fallback: keep drawer open if already in use
          cartEl.open();
        }
      } catch (_e) {
        // ignore
      } finally {
        this._applyingDiscount = false;
        this.setLoadingState(false);
      }
    }

    /**
     * Apply discount code via Ajax Cart API and return updated cart JSON.
     * Does not open/re-render UI; caller can render once with returned sections.
     */
    async applyDiscountForDrawer(cartEl) {
      if (this._applyingDiscount) return null;
      if (this.getCookie(PahiWelcomeDiscount.APPLIED_COOKIE)) return null;

      const rawCode = this.getConfiguredDiscountCode();
      if (!rawCode) return null;

      this._applyingDiscount = true;
      this.setLoadingState(true);

      const cartUpdateUrl =
        (window.routes && window.routes.cart_update_url ? `${window.routes.cart_update_url}.js` : null) ||
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? `${window.Shopify.routes.root}cart/update.js`
          : '/cart/update.js');

      const formData = new FormData();
      formData.append('discount', rawCode);

      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cartEl.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      if (config.headers['Content-Type']) delete config.headers['Content-Type'];
      config.body = formData;

      try {
        const res = await fetch(cartUpdateUrl, config);
        const data = await res.json();
        if (data && data.status) return null;
        this.setCookie(PahiWelcomeDiscount.APPLIED_COOKIE, 'true', { days: 1 });
        return data || null;
      } catch (_e) {
        return null;
      } finally {
        this._applyingDiscount = false;
        this.setLoadingState(false);
      }
    }

    async clearDiscountFromCart() {
      if (this._clearingDiscount) return;
      this._clearingDiscount = true;
      this.setLoadingState(true);

      const cartUpdateUrl =
        (window.routes && window.routes.cart_update_url ? `${window.routes.cart_update_url}.js` : null) ||
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? `${window.Shopify.routes.root}cart/update.js`
          : '/cart/update.js');

      const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('discount', '');

      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cartEl.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      if (config.headers['Content-Type']) delete config.headers['Content-Type'];
      config.body = formData;

      try {
        const res = await fetch(cartUpdateUrl, config);
        const data = await res.json();
        if (data && data.sections && cartEl && typeof cartEl.renderContents === 'function') {
          cartEl.renderContents(data);
        } else {
          const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
          fetch(`${cartUrl}?section_id=cart-drawer`, { method: 'GET', credentials: 'same-origin' })
            .then((r) => r.text())
            .then((htmlText) => {
              const htmlDoc = new DOMParser().parseFromString(htmlText, 'text/html');
              const incomingDrawer = htmlDoc.querySelector('cart-drawer');
              const currentDrawer = document.querySelector('cart-drawer');
              if (incomingDrawer && currentDrawer) {
                currentDrawer.innerHTML = incomingDrawer.innerHTML;
              }
            })
            .catch(() => {});
        }
      } catch (_e) {
        // ignore
      } finally {
        this._clearingDiscount = false;
        this.setLoadingState(false);
      }
    }

    getCookie(name) {
      const key = `${encodeURIComponent(name)}=`;
      const parts = document.cookie ? document.cookie.split('; ') : [];
      for (const part of parts) {
        if (part.indexOf(key) === 0) return decodeURIComponent(part.slice(key.length));
      }
      return null;
    }

    setCookie(name, value, { days = 7 } = {}) {
      const maxAge = Math.max(0, Math.floor(days * 86400));
      document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
        value
      )}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
    }

    removeCookie(name) {
      document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax`;
    }
  }

  const start = () => new PahiWelcomeDiscount().init();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Minimal interop for product-form.js to delay drawer render only when needed.
  window.pahiWelcomeDiscountInterop = window.pahiWelcomeDiscountInterop || {
    shouldDelayDrawerRender() {
      const settings = window.pahiWelcomeDiscount || {};
      if (settings.enabled !== true) return false;
      if (settings.code == null || String(settings.code).trim() === '') return false;

      // Must have welcome cookie and not already applied
      const hasWelcome = document.cookie.includes(`${encodeURIComponent(PahiWelcomeDiscount.COOKIE)}=`);
      const hasApplied = document.cookie.includes(`${encodeURIComponent(PahiWelcomeDiscount.APPLIED_COOKIE)}=`);
      if (!hasWelcome || hasApplied) return false;

      // Ineligible logged-in customers should not delay; they will be handled by main flow
      const customer = window.pahiCustomer || { loggedIn: false, ordersCount: 0 };
      const loggedIn = !!customer.loggedIn;
      const ordersCount = Number(customer.ordersCount || 0);
      if (loggedIn && ordersCount >= 1) return false;

      return true;
    },
    applyDiscountForDrawer(cartEl) {
      return new PahiWelcomeDiscount().applyDiscountForDrawer(cartEl);
    },
  };
})();

